import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';

// ─────────────────────────────────────────────────────────────
// ErrorBoundary — catches any uncaught render error in App and
// shows a recovery UI instead of leaving the user with a blank
// page. Without this, a single thrown exception during render
// unmounts the entire React tree and the user sees nothing.
//
// Two recovery paths are exposed:
//   1) Reload the page — handles transient network or state
//      issues (e.g. a half-loaded Supabase session).
//   2) Clear local data — wipes the localStorage cache and
//      sessionStorage flags. Use when the cached state itself
//      is what's crashing the render (corrupted JSON, schema
//      mismatch from an older deploy, etc).
//
// The actual error + component stack render below the buttons
// so the next time someone hits a blank screen we'll have
// diagnostic info to fix it.
// ─────────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    this.setState({ info });
    // Log to the browser console so it shows up in DevTools and
    // in any error-monitoring extension the user might have on.
    // eslint-disable-next-line no-console
    console.error('[LoanFlow] Render crash caught by ErrorBoundary:', error, info);
  }
  reload = () => { window.location.reload(); };
  resetLocal = () => {
    try {
      localStorage.removeItem('loanflow_v1');
      sessionStorage.clear();
    } catch (e) {}
    window.location.reload();
  };
  render() {
    if (!this.state.error) return this.props.children;
    const styles = {
      page: { minHeight: '100vh', background: '#06080F', color: '#E8ECF4', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' },
      card: { maxWidth: 560, width: '100%', background: '#161E32', border: '1px solid #1F2940', borderRadius: 14, padding: '32px 28px' },
      h1: { fontSize: 20, fontWeight: 700, color: '#6BA3FF', margin: '0 0 8px' },
      p: { fontSize: 14, color: '#8C9BB5', lineHeight: 1.6, margin: '0 0 14px' },
      btnRow: { display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' },
      btnPrimary: { flex: '1 1 200px', padding: '12px 18px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#4F8EF7,#2B6AD8)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
      btnGhost: { flex: '1 1 200px', padding: '12px 18px', borderRadius: 10, border: '1px solid #4F8EF7', background: 'transparent', color: '#6BA3FF', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
      details: { marginTop: 22, padding: '12px 14px', background: '#06080F', border: '1px solid #1F2940', borderRadius: 8, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#8C9BB5', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 260, overflow: 'auto' },
    };
    const errMsg = (this.state.error && (this.state.error.message || String(this.state.error))) || 'Unknown error';
    const stack = (this.state.info && this.state.info.componentStack) || (this.state.error && this.state.error.stack) || '';
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.h1}>Something went wrong loading LoanFlow</h1>
          <p style={styles.p}>The app hit an error during startup. Try reloading first — if the same screen comes back, click <strong style={{color:'#E8ECF4'}}>Reset local data</strong> to clear the cached copy on this device. Your Supabase data is untouched either way.</p>
          <div style={styles.btnRow}>
            <button style={styles.btnPrimary} onClick={this.reload}>Reload</button>
            <button style={styles.btnGhost} onClick={this.resetLocal}>Reset local data</button>
          </div>
          <div style={styles.details}>
            <strong>Error:</strong> {errMsg}
            {stack ? '\n\n' + stack : ''}
          </div>
        </div>
      </div>
    );
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
